import { getSignInData, signIn } from "@/apis/bonus"
import { invalidateCoinBalance } from "@/apis/query/profile"
import { useToast } from "@/provider/ToastProvider"
import { useCoinBalance } from "@/hooks/useCoinBalance"
import { useUserDetailStore } from "@/store/useUserDetailStore"
import { DarkButton } from "@/ui/Button"
import { Check } from "@/ui/icons/Check"
import { ChevronLeft } from "@/ui/icons/ChevronLeft"
import { Energy } from "@/ui/icons/Energy"
import { Luxury } from "@/ui/icons/Luxury"
import { Synergy } from "@/ui/icons/Synergy"
import { useFirebaseAnalytics } from "@/vendor/firebase/analytics"
import { Modal, cn, ModalContent, ModalBody, Button, Image } from "@heroui/react"
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query"
import { AnimatePresence, motion } from "framer-motion"
import { useTranslations } from "next-intl"
import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react"
import NextImage from "next/image"
import { useShowPricing } from "@/components/Pricing"
import { Pending } from "@/components/Pending"

const HOVER_CARD_WIDTH = 280
const HOVER_LEAVE_MS = 100
const HOVER_MOVE_SPRING = { type: "spring" as const, stiffness: 420, damping: 34, mass: 0.8 }

export default ({ onClose, isOpen, onOpenChange }) => {
  const t = useTranslations()
  const showPricing = useShowPricing()

  const { showToast } = useToast()
  const quertyClient = useQueryClient()
  const [isVip, vipType] = useUserDetailStore(state => [state.vip, state.vip_type])
  const { coin } = useCoinBalance()
  const { logEarnVirtualCurrency } = useFirebaseAnalytics()

  // 获取签到数据
  const signInData = useQuery({
    queryKey: ['sign', 'data'],
    queryFn: () => getSignInData(),
    enabled: isOpen
  })


  const onPressSignIn = () => {
    signInMutation.mutate({}, {
      onSuccess(data) {
        if (data.status === 200) {
          quertyClient.refetchQueries({ queryKey: ['sign', 'data'] })
          quertyClient.refetchQueries({ queryKey: ['user', 'detail'] })
          invalidateCoinBalance(quertyClient)
          // 会员按自身档位倍率获得能量
          const receiveCoins = Math.round(getRewardCoin(signRecord[data.data.sign_data.findLastIndex(item => item)]) * ownMultiplier)
          // 发送firebase记录
          logEarnVirtualCurrency(receiveCoins)
        } else {
          showToast({ message: data.msg, type: 'error' })
        }
      }
    })
  }

  // 倒计时
  const [countdown, setCountdown] = useState(0)

  useEffect(() => {
    setCountdown(signInData.data?.data.countdown)

    let timer

    if (signInData.data?.data.countdown !== 0) {
      timer = setInterval(() => {
        setCountdown(pre => {
          if (pre > 0)
            return pre - 1
          else {
            clearInterval(timer)
            timer = null
            return 0
          }
        })
      }, 1000)
    }

    return () => {
      timer && clearInterval(timer)
    }
  }, [signInData.data])

  // 签到
  const signInMutation = useMutation({
    mutationFn: signIn
  })

  const config = signInData.data?.data?.sign_in_config

  // sign_in_reward 的每一项可能是数字，也可能是 { coin } 对象，统一取出金币数量
  const getRewardCoin = (item: any) => {
    if (item && typeof item === 'object') return Number(item.coin) || 0
    return Number(item) || 0
  }

  const signRecord = useMemo(() => {
    const signInReward = config?.sign_in_reward
    if (signInReward) {
      return Object.values(signInReward) as any[]
    }
    return []
  }, [signInData])

  // 已连续签到天数
  const signedCount = useMemo(() => {
    const signData = signInData.data?.data?.sign_data
    if (Array.isArray(signData)) return signData.filter(Boolean).length
    return Number(signData) || 0
  }, [signInData])

  // luxury(vip_normal) / synergy(vip_union) 会员能量倍率
  const vipNormal = Number(config?.vip_normal) || 2
  const vipUnion = Number(config?.vip_union) || 2
  const isSynergy = isVip && vipType === 2
  // Luxury 会员：premium 按钮引导升级到更高档的 Synergy
  const isLuxury = isVip && !isSynergy
  // 上层卡：会员显示自己档位图标与倍率后的能量，非会员显示能量图标与基础能量
  const ownMultiplier = isVip ? (isSynergy ? vipUnion : vipNormal) : 1
  const OwnVipIcon = isSynergy ? Synergy : Luxury
  // 下层卡：非会员预览 Luxury 档(vip_normal)，会员展示 Synergy 档(vip_union)；仅 Synergy 会员点亮
  const bottomMultiplier = isVip ? vipUnion : vipNormal
  const BottomVipIcon = isVip ? Synergy : Luxury

  const totalDays = signRecord.length || 7

  // 单例 hover 卡：日期间位移动画，避免各自显隐闪烁
  const [hoverDay, setHoverDay] = useState<number | null>(null)
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 })
  const listRef = useRef<HTMLDivElement>(null)
  const dayRefs = useRef<(HTMLLIElement | null)[]>([])
  const hoverCardRef = useRef<HTMLDivElement>(null)
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const measureHoverPos = (index: number, cardH = 280) => {
    const list = listRef.current
    const dayEl = dayRefs.current[index]
    if (!list || !dayEl) return null

    const listRect = list.getBoundingClientRect()
    const dayRect = dayEl.getBoundingClientRect()
    const col = index % 4
    const isLast = index === signRecord.length - 1
    const placeBelow = index < 4

    let x: number
    if (col === 0) {
      x = dayRect.left - listRect.left
    } else if (isLast || col === 3) {
      x = dayRect.right - listRect.left - HOVER_CARD_WIDTH
    } else {
      x = dayRect.left - listRect.left + dayRect.width / 2 - HOVER_CARD_WIDTH / 2
    }

    // 对齐原 CSS：下方 -mt-5 / 上方 -mb-5（20px 重叠）
    const y = placeBelow
      ? dayRect.bottom - listRect.top - 20
      : dayRect.top - listRect.top + 20 - cardH

    return { x, y }
  }

  const clearHoverLeave = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current)
      leaveTimerRef.current = null
    }
  }

  const openHoverDay = (index: number) => {
    clearHoverLeave()
    const next = measureHoverPos(index, hoverCardRef.current?.offsetHeight || 280)
    if (next) setHoverPos(next)
    setHoverDay(index)
  }

  const scheduleCloseHover = () => {
    clearHoverLeave()
    leaveTimerRef.current = setTimeout(() => setHoverDay(null), HOVER_LEAVE_MS)
  }

  useEffect(() => () => clearHoverLeave(), [])

  useEffect(() => {
    if (!isOpen) {
      clearHoverLeave()
      setHoverDay(null)
    }
  }, [isOpen])

  // 内容高度变化（如第 7 天含头像框）后用真实高度校正 y
  useLayoutEffect(() => {
    if (hoverDay === null) return
    const next = measureHoverPos(hoverDay, hoverCardRef.current?.offsetHeight || 280)
    if (!next) return
    setHoverPos((prev) => (prev.x === next.x && prev.y === next.y ? prev : next))
  }, [hoverDay, signRecord.length, isSynergy, isVip])

  const formatterCountdown = useMemo(() => {
    const hours = String(Math.floor(countdown / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((countdown % 3600) / 60)).padStart(2, '0');
    const secs = String(countdown % 60).padStart(2, '0');

    return `${hours}:${minutes}:${secs}`;
  }, [countdown])


  return <Modal
    classNames={{
      base: cn(
        "bg-[url('/imgs/claim-bg.png')] bg-no-repeat bg-contain dark:!bg-none dark:bg-surface",
        'max-w-[520px] w-full rounded-[16px]',
      ),
      body: 'w-[344px] p-0 mx-auto pb-10'
    }}
    onOpenChange={onOpenChange}
    isOpen={isOpen}>
    <ModalContent className="text-center">
      <ModalBody>
        <h1 className="text-[48px] font-bold text-center mt-12 leading-tight">{coin}</h1>
        <p className="text-theme-primary/70 mt-2">{t('bonus.myEnergy')}</p>

        <div className="mt-3 flex gap-3 justify-center">
          <DarkButton onPress={() => showPricing('energy')} className="h-8 bg-theme-primary/5 text-theme-primary font-bold gap-0 pl-5 pr-2">
            <span>{t('bonus.buy')}</span>
            <ChevronLeft className="rotate-180" width={16} />
          </DarkButton>
          <DarkButton onPress={() => showPricing('premium')} className="h-8 bg-theme-primary/5 text-theme-primary font-bold gap-0 pl-2 pr-2">
            <NextImage className="ml-1 mb-[2px]" src={isLuxury ? "/icons/Synergy.svg" : "/icons/Luxury.svg"} width={20} height={20} alt={isLuxury ? "Synergy" : "Luxury"} loading="eager" />
            <span>{isLuxury ? t('bonus.synergy') : t('bonus.premium')}</span>
            <ChevronLeft className="rotate-180" width={16} />
          </DarkButton>
        </div>

        {
          signInData.isPending ?
            <Pending /> :
            <>
              {/* 连续签到进度条 */}
              <div className="relative mt-6 h-8">
                {/* 轨道 */}
                <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[4px] rounded-full bg-theme-primary/[0.06]" />
                {/* 已签到进度填充 */}
                {
                  signedCount > 0 &&
                  <div
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-[4px] rounded-[4px] bg-[#FFD84C]"
                    style={{ width: `calc(${((Math.min(signedCount, totalDays) - 0.5) / totalDays) * 100}% + 20px)`, maxWidth: '100%' }}
                  />
                }
                {/* 每日节点：已签到为打勾圆，未签到为灰点，最后一天为礼盒 */}
                {
                  Array.from({ length: totalDays }).map((_, i) => {
                    const centerPct = ((i + 0.5) / totalDays) * 100

                    if (i === totalDays - 1) {
                      return (
                        <NextImage
                          key={i}
                          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
                          style={{ left: `${centerPct}%` }}
                          src="/imgs/sign-gift.png"
                          width={32}
                          height={32}
                          alt="gift"
                        />
                      )
                    }

                    return i < signedCount
                      ? <div
                        key={i}
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-[18px] h-[18px] rounded-full bg-[#FFD84C] flex items-center justify-center text-theme-white"
                        style={{ left: `${centerPct}%` }}
                      >
                        <Check width={9} />
                      </div>
                      : <div
                        key={i}
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-[10px] h-[10px] rounded-full bg-theme-primary/[0.08]"
                        style={{ left: `${centerPct}%` }}
                      />
                  })
                }
              </div>

              <p className="text-[14px] text-theme-primary mt-4 text-center">
                {t.rich('bonus.continuousSignIn', {
                  count: signedCount,
                  b: (chunks) => <span className="text-[#FF780D] font-bold text-[16px]">{chunks}</span>
                })}
              </p>

              {/* 悬浮卡放在 grid 外，避免作为 ul 子项挤占 Day7 的 col-span */}
              <div
                ref={listRef}
                className="relative w-full mx-auto mt-5"
                onMouseLeave={scheduleCloseHover}
              >
              <ul className="grid grid-cols-4 gap-2 w-full">
                {
                  signRecord.map((item, i) => {
                    const claimed = i < signedCount
                    const isLast = i === signRecord.length - 1
                    const baseCoin = getRewardCoin(item)
                    const ownCoin = Math.round(baseCoin * ownMultiplier)
                    const bottomCoin = Math.round(baseCoin * bottomMultiplier)
                    // Synergy 仅最后一天有悬浮详情；其余档位每天可悬停对比
                    const canHover = isSynergy
                      ? isLast && !!config?.continuous_sign_in_reward?.avatar
                      : true

                    // Synergy 会员：单层卡片，只展示自己档位的奖励
                    if (isSynergy) {
                      return (
                        <li
                          key={i}
                          ref={(el) => { dayRefs.current[i] = el }}
                          className="relative flex flex-col last:col-span-2"
                          onMouseEnter={() => canHover && openHoverDay(i)}
                        >
                          <div className={cn(
                            "h-[92px] rounded-[12px] flex items-start justify-center gap-10 pt-5",
                            claimed ? "bg-[#FFEFB7] dark:bg-[#3B3320]" : "bg-light-gray"
                          )}>
                            <div className="flex flex-col items-center gap-1">
                              <span className={cn(
                                "text-[16px] font-bold leading-5",
                                claimed ? "text-[#FF780D]" : "text-theme-primary/70"
                              )}>+ {ownCoin}</span>
                              {
                                claimed
                                  ? <div className="w-6 h-6 rounded-full bg-[#FF780D] flex items-center justify-center text-theme-white">
                                    <Check className="mx-auto" width={12} />
                                  </div>
                                  : <OwnVipIcon width={24} height={24} />
                              }
                            </div>
                            {
                              isLast && config?.continuous_sign_in_reward?.avatar &&
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-[10px] text-theme-white capitalize rounded-[4px] px-1 leading-4 bg-gradient-to-r from-[#9658FA] to-[#4E46FE]">limit</span>
                                <Image
                                  radius="sm"
                                  width={24}
                                  height={24}
                                  src={config.continuous_sign_in_reward.avatar}
                                />
                              </div>
                            }
                          </div>

                          <span className="text-[13px] text-theme-primary/40 font-bold mt-2">Day {i + 1}</span>
                        </li>
                      )
                    }

                    return (
                      <li
                        key={i}
                        ref={(el) => { dayRefs.current[i] = el }}
                        className="relative flex flex-col gap-[2px] last:col-span-2"
                        onMouseEnter={() => openHoverDay(i)}
                      >
                        {/* 当前档位每日奖励 */}
                        <div className={cn(
                          "h-[72px] rounded-t-[12px] flex flex-col items-center justify-between pt-3 pb-2",
                          claimed ? "bg-[#FFEFB7] dark:bg-[#3B3320]" : "bg-light-gray dark:bg-[#222121]"
                        )}>
                          <span className={cn(
                            "text-[16px] font-bold leading-5",
                            claimed ? "text-[#FF780D]" : "text-theme-primary/70"
                          )}>+ {ownCoin}</span>
                          {
                            claimed
                              ? <div className="w-6 h-6 rounded-full bg-[#FF780D] flex items-center justify-center text-theme-white">
                                <Check className="mx-auto" width={12} />
                              </div>
                              : isVip
                                ? <OwnVipIcon width={24} height={24} />
                                : <Energy width={24} height={24} />
                          }
                        </div>

                        {/* 更高档位能量奖励（Synergy 会员点亮，其余置灰） */}
                        <div className={cn(
                          "h-[72px] rounded-b-[12px] bg-light-gray dark:bg-[#464444]",
                          !isSynergy && "opacity-40"
                        )}>
                          {
                            isLast && config?.continuous_sign_in_reward?.avatar
                              ? <div className="h-full flex items-start justify-center gap-10 pt-2">
                                <div className="flex flex-col items-center gap-[10px]">
                                  <BottomVipIcon width={24} height={24} />
                                  <span className="text-[16px] font-bold leading-5 text-theme-primary/70">+ {bottomCoin}</span>
                                </div>
                                <div className="flex flex-col items-center gap-[10px]">
                                  <Image
                                    radius="sm"
                                    width={24}
                                    height={24}
                                    src={config.continuous_sign_in_reward.avatar}
                                  />
                                  <span className="text-[10px] text-theme-white capitalize rounded-[4px] px-1 leading-4 bg-gradient-to-r from-[#9658FA] to-[#4E46FE]">limit</span>
                                </div>
                              </div>
                              : <div className="h-full flex flex-col items-center justify-between pt-2 pb-3">
                                <BottomVipIcon width={24} height={24} />
                                <span className="text-[16px] font-bold leading-5 text-theme-primary/70">+ {bottomCoin}</span>
                              </div>
                          }
                        </div>

                        <span className="text-[13px] text-theme-primary/40 font-bold mt-1">Day {i + 1}</span>
                      </li>
                    )
                  })
                }
              </ul>

                {/* 单例悬浮卡：日期间 spring 平移，进出淡入淡出 */}
                <AnimatePresence>
                  {hoverDay !== null && (() => {
                    const item = signRecord[hoverDay]
                    if (item == null) return null
                    const isLast = hoverDay === signRecord.length - 1
                    const hasContinuousReward = isLast && !!config?.continuous_sign_in_reward?.avatar
                    const baseCoin = getRewardCoin(item)
                    const ownCoin = Math.round(baseCoin * ownMultiplier)
                    const bottomCoin = Math.round(baseCoin * bottomMultiplier)

                    return (
                      <motion.div
                        key="sign-hover-card"
                        ref={hoverCardRef}
                        className={cn(
                          "absolute z-20 pointer-events-none w-[280px] rounded-[16px] overflow-hidden",
                          "bg-theme-white dark:bg-surface-2",
                          "shadow-[0px_0px_8px_0px_rgba(0,0,0,0.25)] dark:shadow-[0px_0px_8px_0px_rgba(0,0,0,0.6)]",
                          isSynergy && "text-center"
                        )}
                        initial={{ opacity: 0, scale: 0.96, x: hoverPos.x, y: hoverPos.y }}
                        animate={{ opacity: 1, scale: 1, x: hoverPos.x, y: hoverPos.y }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={HOVER_MOVE_SPRING}
                        style={{ left: 0, top: 0 }}
                      >
                        {isSynergy ? (
                          <div className="flex flex-col items-center pt-[68px] pb-6">
                            <OwnVipIcon width={24} height={24} />
                            {hasContinuousReward && (
                              <Image classNames={{ wrapper: 'mt-4' }} radius="lg" width={80} height={80} src={config!.continuous_sign_in_reward!.avatar} />
                            )}
                            <span className="mt-4 flex items-center justify-center gap-1 text-[16px] font-bold leading-5 text-theme-primary">
                              <Energy width={16} height={17} />
                              +{ownCoin}
                            </span>
                            {hasContinuousReward && (
                              <>
                                <span className="mt-3 text-[12px] leading-4 capitalize text-t-subtitle">{config?.continuous_sign_in_reward?.name || 'Avatar Frame'}</span>
                                <span className="mt-2 text-[10px] text-theme-white capitalize rounded-[4px] px-1 leading-4 bg-gradient-to-r from-[#9658FA] to-[#4E46FE]">limit</span>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="flex text-center">
                            <div className={cn("flex-1 flex flex-col items-center pt-[68px]", hasContinuousReward ? "pb-6" : "pb-12")}>
                              {
                                isVip
                                  ? <OwnVipIcon width={24} height={24} />
                                  : <BottomVipIcon className="grayscale opacity-70" width={24} height={24} />
                              }
                              {
                                hasContinuousReward
                                  ? <>
                                    {isVip ?
                                      <Image classNames={{ wrapper: 'mt-7' }} radius="lg" width={56} height={56} src={config!.continuous_sign_in_reward!.avatar} /> :
                                      <div className="flex-1"></div>
                                    }
                                    <span className="mt-7 flex items-center gap-1 text-[16px] font-bold leading-5 text-theme-primary">
                                      <Energy width={16} height={17} />
                                      +{ownCoin}
                                    </span>
                                    <span className="mt-3 text-[12px] leading-4 capitalize text-t-subtitle">{config?.continuous_sign_in_reward?.name || 'Avatar Frame'}</span>
                                    <span className="mt-2 text-[10px] text-theme-white capitalize rounded-[4px] px-1 leading-4 bg-gradient-to-r from-[#9658FA] to-[#4E46FE]">limit</span>
                                  </>
                                  : <>
                                    <Energy className="mt-8" width={56} height={56} />
                                    <span className="mt-6 text-[16px] font-bold leading-5 text-theme-primary">+{ownCoin}</span>
                                    <span className="mt-3 text-[12px] leading-4 capitalize text-t-subtitle">Energy</span>
                                  </>
                              }
                            </div>
                            <div className={cn("flex-1 flex flex-col items-center pt-[68px] bg-light-gray dark:bg-[#222121]", hasContinuousReward ? "pb-6" : "pb-12")}>
                              <BottomVipIcon width={24} height={24} />
                              {
                                hasContinuousReward
                                  ? <>
                                    <Image classNames={{ wrapper: 'mt-7' }} radius="lg" width={56} height={56} src={config!.continuous_sign_in_reward!.avatar} />
                                    <span className="mt-7 flex items-center gap-1 text-[16px] font-bold leading-5 text-theme-primary opacity-40">
                                      <Energy width={16} height={17} />
                                      +{bottomCoin}
                                    </span>
                                    <span className="mt-3 text-[12px] leading-4 capitalize text-t-subtitle opacity-40">{config?.continuous_sign_in_reward?.name || 'Avatar Frame'}</span>
                                    <span className="mt-2 text-[10px] text-theme-white capitalize rounded-[4px] px-1 leading-4 bg-gradient-to-r from-[#9658FA] to-[#4E46FE]">limit</span>
                                  </>
                                  : <>
                                    <Energy className="mt-8" width={56} height={56} />
                                    <span className="mt-6 text-[16px] font-bold leading-5 text-theme-primary opacity-40">+{bottomCoin}</span>
                                    <span className="mt-3 text-[12px] leading-4 capitalize text-t-subtitle opacity-40">Energy</span>
                                  </>
                              }
                            </div>
                          </div>
                        )}
                        <span className="absolute top-6 inset-x-0 flex justify-center">
                          <span className="h-6 px-3 rounded-[14px] bg-[#001D35] dark:bg-[#12466E] text-theme-white text-[12px] leading-6 whitespace-nowrap">
                            Day {hoverDay + 1}
                          </span>
                        </span>
                      </motion.div>
                    )
                  })()}
                </AnimatePresence>
              </div>
            </>
        }

        {countdown === 0 ?
          <Button
            className="bg-gradient-to-r from-[#FFBE3F] to-[#FFD74B] h-12 mt-6 text-theme-black font-bold"
            radius="full"
            isLoading={signInMutation.isPending}
            isDisabled={signInData.isPending}
            onPress={onPressSignIn}
          >{t('bonus.claim')}</Button>
          :
          <Button
            className="bg-[#9B9B9B]/40 h-12 mt-6 text-theme-primary/70 font-bold pointer-events-none gap-2"
            radius="full"
            isLoading={signInMutation.isPending}
            onPress={onPressSignIn}
          >
            <div className="w-6 h-6 rounded-full bg-[#FF780D] flex items-center justify-center text-theme-white">
              <Check className="mx-auto" width={12} />
            </div>
            {formatterCountdown}</Button>
        }

      </ModalBody>
    </ModalContent>
  </Modal>
}
